import { getSlideManifest, getSlideSlugByKey } from "@-/dev.improve.haniverse.assets.slides";
import type {
  AnalyticsEventName,
  AnalyticsEventProperties,
} from "@-/dev.improve.haniverse.lib.analytics";
import type { LearnCardActionSnapshot } from "#execute-learn-card-action";
import { learnCardActionLabel } from "#learn-card-action";
import type { LearnCardActionReceipt } from "#learn-card-receipt";

export type LearnCardAnalyticsEvent = {
  [Name in AnalyticsEventName]: { name: Name; properties: AnalyticsEventProperties[Name] };
}[AnalyticsEventName];

/**
 * The product events a saved card action stands for. Pure: the caller captures them after the
 * write committed, deduplicated by the receipt's operation id (a retry returns the same receipt
 * kind, and a review retry reports `alreadyPersisted`, which records nothing here).
 */
export function learnCardReceiptAnalyticsEvents({
  isRedo,
  receipt,
  snapshot,
  trigger,
}: {
  isRedo: boolean;
  receipt: LearnCardActionReceipt;
  snapshot: LearnCardActionSnapshot;
  trigger: string;
}): LearnCardAnalyticsEvent[] {
  const { action, persisted } = receipt;
  const rating = action.kind === "rate" ? learnCardActionLabel(action) : action.kind;
  switch (persisted.kind) {
    case "review": {
      if (persisted.review.alreadyPersisted) {
        return [];
      }
      return [
        {
          name: "card_reviewed",
          properties: {
            drill_type: persisted.drillType.kind,
            grapheme: persisted.grapheme,
            is_redo: isRedo,
            kind: "card",
            rating,
            review_count: 1,
            trigger,
          },
        },
        ...familiarEvents(persisted.familiarGrapheme === null ? [] : [persisted.familiarGrapheme]),
      ];
    }
    case "review-batch": {
      const landed = persisted.reviews.filter((review) => !review.alreadyPersisted);
      if (landed.length === 0) {
        return [];
      }
      return [
        {
          name: "card_reviewed",
          properties: {
            drill_type: snapshot.kind === "sentence" ? snapshot.drillType.kind : "sentence",
            grapheme: persisted.word,
            is_redo: isRedo,
            kind: "sentence",
            rating,
            review_count: landed.length,
            trigger,
          },
        },
        ...familiarEvents(persisted.familiarGraphemes),
      ];
    }
    case "enrollment": {
      if (!persisted.wasAdded) {
        return [];
      }
      return [
        {
          name: "character_learned",
          properties: {
            grapheme: persisted.grapheme,
            is_redo: isRedo,
            level_index: snapshot.kind === "lesson" ? snapshot.levelIndex : null,
            part: snapshot.kind === "lesson" ? (snapshot.part ?? null) : null,
            trigger,
          },
        },
      ];
    }
    case "marked-known": {
      return [
        {
          name: "character_marked_known",
          properties: { grapheme: persisted.grapheme, is_redo: isRedo, trigger },
        },
      ];
    }
    case "skipped": {
      return [
        {
          name: "lesson_skipped",
          properties: { grapheme: persisted.grapheme, is_redo: isRedo, trigger },
        },
      ];
    }
    case "deferral": {
      return [
        {
          name: "lesson_deferred",
          properties: { grapheme: persisted.grapheme, is_redo: isRedo, trigger },
        },
      ];
    }
    case "quiz-answer": {
      if (persisted.review.alreadyPersisted || snapshot.kind !== "quiz") {
        return [];
      }
      return [
        {
          name: "quiz_answered",
          properties: {
            correct: action.kind === "answer-quiz" && action.correct,
            is_redo: isRedo,
            quiz_slug: snapshot.quizSlug,
            source: snapshot.source,
            trigger,
          },
        },
      ];
    }
    case "slide-completion": {
      const slideSlug =
        snapshot.kind === "slide" ? snapshot.slideSlug : getSlideSlugByKey(persisted.slideKey);
      if (slideSlug === undefined) {
        return [];
      }
      const manifestIndex = getSlideManifest().findIndex(
        (entry) => entry.key === persisted.slideKey,
      );
      return [
        {
          name: "slide_completed",
          properties: {
            is_redo: isRedo,
            manifest_index: manifestIndex === -1 ? null : manifestIndex,
            slide_slug: slideSlug,
          },
        },
      ];
    }
    case "none": {
      return [];
    }
  }
}

function familiarEvents(graphemes: readonly string[]): LearnCardAnalyticsEvent[] {
  return graphemes.map((grapheme) => ({
    name: "character_became_familiar",
    properties: { grapheme },
  }));
}
